import {
  ErrorBox,
  GET_METRIC_BY_METRIC_ID_AND_ENVS,
  GET_POLICY_BY_POLICY_ID,
  toGovernanceMetric,
  toGovernancePolicy,
} from '@agilelab/plugin-wb-governance';
import {
  GovernanceEntityType,
  MetricByIdQuery,
  PoliciesByIdsQuery,
} from '@agilelab/plugin-wb-governance-common';
import {
  TestStatus,
  WbDrawer,
  WbDrawerContent,
} from '@agilelab/plugin-wb-platform';
import { useQuery } from '@apollo/client';
import { ErrorPanel, Progress } from '@backstage/core-components';
import { Box, DrawerProps, makeStyles, Typography } from '@material-ui/core';
import { useMemo, useState } from 'react';
import { Task } from '../../types';
import { MetricDetailDrawerContent } from '../../../common';
import { PolicyDetailDrawerContent } from '../../../common/PolicyDetailDrawerContent';

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    height: '100%',
    width: '50%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  chip: {
    margin: 0,
  },
}));

const ErrorBoxWrapper = (props: { errors: string[] }) => {
  const { errors } = props;
  const [currentErrorIndex, setCurrentErrorIndex] = useState<number>(0);
  const handleSwitch = (index: number) => {
    setCurrentErrorIndex(index);
  };

  return (
    <ErrorBox
      error={{
        message: errors.at(currentErrorIndex)!,
      }}
      navigation={{
        current: currentErrorIndex,
        total: errors.length,
        handleSwitch: handleSwitch,
      }}
      headerTitle="Error Info"
    />
  );
};

export const TaskDrawer = (
  props: DrawerProps & {
    task: Task | null;
    onClose: (open: boolean) => void;
  },
) => {
  const { task, ...drawerProps } = props;
  const classes = useStyles();

  const {
    data: policyData,
    error: policyError,
    loading: loadingPolicy,
  } = useQuery<PoliciesByIdsQuery>(GET_POLICY_BY_POLICY_ID, {
    variables: { policyId: task?.governanceEntityId },
    skip: task?.governanceEntityId
      ? task.governanceEntityType !== GovernanceEntityType.Policy
      : true,
  });

  const {
    data: metricData,
    error: metricError,
    loading: loadingMetric,
  } = useQuery<MetricByIdQuery>(GET_METRIC_BY_METRIC_ID_AND_ENVS, {
    variables: { metricId: task?.governanceEntityId },
    skip: task?.governanceEntityId
      ? task.governanceEntityType !== GovernanceEntityType.Metric
      : true,
  });

  // Replace Policy Status with the one returned from the test
  const policy = useMemo(() => {
    const fetchedPolicy =
      policyData &&
      policyData.cgp_governance_entity[0] &&
      toGovernancePolicy(policyData.cgp_governance_entity[0]);
    if (!fetchedPolicy) return fetchedPolicy;
    return {
      ...fetchedPolicy,
      ...(task?.governanceEntityStatus
        ? { status: task.governanceEntityStatus }
        : {}),
    };
  }, [policyData, task]);

  // Replace Metric Status with the one returned from the test
  const metric = useMemo(() => {
    const fetchedPolicy =
      metricData &&
      metricData.cgp_governance_entity[0] &&
      toGovernanceMetric(metricData.cgp_governance_entity[0]);
    if (!fetchedPolicy) return fetchedPolicy;
    return {
      ...fetchedPolicy,
      ...(task?.governanceEntityStatus
        ? { status: task.governanceEntityStatus }
        : {}),
    };
  }, [metricData, task]);
  return (
    <WbDrawer
      open={props.open!}
      setOpen={props.onClose}
      title="Test Details"
      {...drawerProps}
    >
      <WbDrawerContent>
        {task && (
          <>
            {(loadingPolicy || loadingMetric) && <Progress />}
            {(metricError || policyError) && (
              <ErrorPanel error={metricError || policyError!} />
            )}

            {metric && (
              <MetricDetailDrawerContent task={task} metric={metric} />
            )}

            {policy && (
              <PolicyDetailDrawerContent task={task} policy={policy} />
            )}

            {!task.governanceEntityId && (
              <>
                <Box className={classes.header}>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <TestStatus status={task.status} variant="reduced" />
                    <Typography
                      variant="h6"
                      style={{ fontWeight: '700', marginBottom: 0 }}
                    >
                      {task.name}
                    </Typography>
                  </Box>
                </Box>

                {task.errors.length > 0 && (
                  <ErrorBoxWrapper errors={task.errors} />
                )}
              </>
            )}
          </>
        )}
      </WbDrawerContent>
    </WbDrawer>
  );
};
